import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { NAV_BAR_ENTRIES, NAV_GROUPS, navGroupForKey, navKeyForRoute, type NavGroupKey } from '../nav';
import { usePortalConfig } from '../config/PortalConfigProvider';
import { useSession } from '../session/SessionProvider';
import type { Navigation, Route } from '../router';
import { routePath } from '../router';
import type { PASubscriber } from '../api/pa';

/**
 * The authenticated chrome: header with the identity, the section list, the page, the
 * footer (`ui-enduser-personal-area` §"Global chrome").
 *
 * # What of §"Global chrome" is here and what is not
 *
 * HERE: the product title, the subscriber's name, «Выйти», the section list, a footer.
 *
 * NOT HERE, each because the thing behind it does not exist in this build — the hamburger
 * (§"Global chrome" collapses a left SIDEBAR; ~~and this portal has a horizontal strip that
 * scrolls instead: at 360 px a drawer is a thing nobody opens, and with ten sections the
 * strip is still one gesture away from every one of them~~ *(struck 2026-08-14 — the strip
 * grew to twenty-one sections and the «one gesture» became a HORIZONTAL SCROLLBAR on every
 * page, which is the defect the customer reported. The hamburger is STILL absent and for the
 * unchanged reason — at 360 px a drawer is a thing nobody opens — but what replaced the strip
 * is a grouped, WRAPPING menu bar, see «The section list» below)*); the LANGUAGE dropdown
 * (ST13 ships RU only); the
 * NOTIFICATIONS BELL (the message domain has no subscriber-facing client yet); the
 * messenger-bot shortcuts and the operator's brandable LOGO (an image the walled garden
 * could not fetch, and no upload endpoint exists);
 * the BREADCRUMB (a trail of one is noise); the ✉ EMAIL beside the name (`/pa/auth/me`
 * returns no contact, deliberately — «no ИНН, no bank details, no identity document»).
 *
 * ⚠ THE OPERATOR'S OWN TEXT ARRIVED IN W3. `GET /pa/config` (D258) publishes the product
 * title, the support telephone and email, the company block and the copyright line, and
 * the header and footer render them — ~~all operator-configured, and this listener exposes
 * no public configuration read~~ *(struck 2026-08-12)*. Every one is rendered ONLY when the
 * operator has set it: an empty value produces no element at all, because inventing a
 * telephone number or a company name is worse than omitting one. When he has written no
 * support contact the footer falls back to the one true thing it can say without
 * configuration — «the number in your contract».
 *
 * # The section list — a GROUPED, WRAPPING menu bar (2026-08-14)
 *
 * Rendered from `NAV_BAR_ENTRIES` + `NAV_GROUPS`, both derived from `NAV_ENTRIES`, which has
 * one row per BUILT section — see nav.ts for the rule, why it is a rule, and why the sections
 * hang where they do. The leaves are real `<a href>` links so a long-press can copy one and a
 * middle-click opens it, with `preventDefault` for the in-page navigation: `mountSPA` serves
 * index.html for any non-file path, so even a hard navigation lands correctly.
 *
 * ## Why a disclosure menu and not the four alternatives
 *
 * The complaint was a HORIZONTAL SCROLLBAR across the top of every page: twenty-one `nowrap`
 * links in a `max-width: 880px` flex row with no `flex-wrap` overflow every viewport there is.
 * Four repairs were on the table and three were rejected:
 *
 *  - **`flex-wrap: wrap` and nothing else.** One line, no interaction cost, and it does remove
 *    the scrollbar — but twenty-one links wrap to SEVEN rows at 360 px, so the page a
 *    suspended subscriber opens is a wall of links with the balance below the fold. It also
 *    leaves the OTHER half of the complaint («long menus… find a better design approach»)
 *    untouched: twenty-one undifferentiated words are a findability problem, not a width one.
 *  - **A priority-plus «Ещё» overflow.** Removes the scrollbar and nothing else: it needs
 *    JavaScript measurement of rendered widths (which jsdom cannot express, so it would ship
 *    untested), it hides a DIFFERENT arbitrary tail at every viewport width, and «Ещё» is a
 *    label that tells a subscriber nothing about what is behind it.
 *  - **A hamburger drawer**, which `ui-enduser-personal-area` §"Global chrome" draws. Rejected
 *    for the reason this file has always given: at 360 px a drawer is a thing nobody opens,
 *    and it puts EVERY section — including the landing page — behind one press.
 *  - **A grouped disclosure bar** — chosen. Five named groups plus the landing link is SIX
 *    controls, which fits one row above 720 px and two rows at 360 px; every section is at
 *    most one press further than it was; and the group names give a subscriber who does not
 *    know a section's name somewhere to look, which is the half of the complaint a width fix
 *    alone would have missed.
 *
 * ## What makes it accessible, and what would silently break it
 *
 * The pattern is WAI-ARIA APG's **Disclosure Navigation Menu**, deliberately NOT `role=
 * "menubar"`: a real menubar owes roving `tabindex`, and a roving `tabindex` that is subtly
 * wrong is worse for a keyboard user than plain tab order. Here every control — the landing
 * link, the five buttons, and the links of the OPEN panel — is an ordinary tab stop. Enter and
 * Space toggle (native `<button>`); ↓/↑ open a panel and land on its first/last row; ↓/↑/Home/
 * End move within it; Escape closes and returns focus to the button that opened it.
 *
 * ⚠ **A CLOSED PANEL IS HIDDEN BY THE `hidden` ATTRIBUTE AND NEVER BY CSS ALONE**, and that is
 * load-bearing twice over. In the browser, a panel hidden only by a class is still in the
 * accessibility tree, so a screen-reader user tabs through twenty invisible links. In the
 * TESTS it is worse than that: `vitest.config.ts` sets `css: false`, so `global.css` is not
 * applied under jsdom at all — a CSS-only hide would leave every link queryable, and every arm
 * asserting «this section is one press away» would pass without a single press. The `hidden`
 * attribute is honoured by both. `.pa-nav-panel[hidden] { display: none }` is in `global.css`
 * for the same reason in reverse: a `display: flex` rule would otherwise defeat the attribute.
 *
 * ⚠ **THE ACTIVE GROUP IS OPEN BY DEFAULT.** That is what keeps «the section list is the only
 * place this portal tells him where he is» true after grouping: arriving on `/usage` he sees
 * «Счёт» marked AND «Расход средств» marked inside it, having pressed nothing. The open panel
 * follows the route (the effect below re-syncs on every navigation) until he opens another
 * himself, and on the landing page nothing is open at all, because `home` is its own control.
 */
/**
 * The two DOM ids the disclosure pattern needs, in ONE place each.
 *
 * They are functions rather than inline template strings because `aria-controls` on the button
 * and `id` on the panel MUST agree, and `aria-labelledby` on the panel must name the button —
 * three references to two ids, which is exactly the shape that drifts when it is spelt out
 * three times. An `aria-controls` pointing at nothing is silently inert.
 */
const panelDomId = (group: NavGroupKey) => `pa-nav-panel-${group}`;
const buttonDomId = (group: NavGroupKey) => `pa-nav-group-${group}`;

export function PortalShell({
  subscriber,
  route,
  navigate,
  children,
}: {
  subscriber: PASubscriber;
  route: Route;
  navigate: Navigation['navigate'];
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { signOut } = useSession();
  const config = usePortalConfig();
  const [leaving, setLeaving] = useState(false);
  const active = navKeyForRoute(route);
  const activeGroup = navGroupForKey(active);
  // The panel that is open. It STARTS as the active section's group and is re-synced on every
  // navigation, so the subscriber always sees where he is; opening another group overrides it
  // until he moves.
  const [openGroup, setOpenGroup] = useState<NavGroupKey | null>(activeGroup);
  useEffect(() => {
    setOpenGroup(navGroupForKey(active));
  }, [active]);

  const groupButtons = useRef<Partial<Record<NavGroupKey, HTMLButtonElement | null>>>({});
  // ⚠ A KEYBOARD OPEN CANNOT FOCUS THE ROW IT IS OPENING: at the moment of the keystroke the
  // panel is still `hidden`, and `focus()` on a hidden element does nothing. So the intent is
  // parked here and consumed by the effect that runs AFTER the commit that reveals it.
  const pendingEdge = useRef<'first' | 'last' | null>(null);

  const name = subscriber.display_name || subscriber.login;
  // The operator's own product name when he has set one; the generic «Личный кабинет»
  // otherwise. Nothing is invented in between.
  const title = config.product_title || t('brand');

  const go = (event: React.MouseEvent, target: Route) => {
    // Let a modified click (new tab, download) behave like an ordinary link.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    navigate(target);
  };

  const rowsIn = (group: NavGroupKey): HTMLAnchorElement[] => {
    const panel = document.getElementById(panelDomId(group));
    return panel ? Array.from(panel.querySelectorAll<HTMLAnchorElement>('a')) : [];
  };

  const focusRow = (group: NavGroupKey, index: number) => {
    const rows = rowsIn(group);
    if (rows.length === 0) return;
    // CLAMPED, not wrapped: ↓ at the last row is a no-op, which is what a tab-order menu does.
    // A wrap here would jump a keyboard user back to the top with no announcement.
    const at = Math.min(Math.max(index, 0), rows.length - 1);
    rows[at]?.focus();
  };

  const focusEdge = (group: NavGroupKey, edge: 'first' | 'last') => {
    focusRow(group, edge === 'first' ? 0 : rowsIn(group).length - 1);
  };

  useEffect(() => {
    if (openGroup === null || pendingEdge.current === null) return;
    const edge = pendingEdge.current;
    pendingEdge.current = null;
    focusEdge(openGroup, edge);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openGroup]);

  const closeAndReturnFocus = (group: NavGroupKey) => {
    // The button first, THEN the state: the row holding focus is about to be hidden, and a
    // `hidden` ancestor sends focus to <body> — which on a phone with a keyboard means the
    // next Tab starts the page again from the top.
    groupButtons.current[group]?.focus();
    setOpenGroup(null);
  };

  const onGroupKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, group: NavGroupKey) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const edge = event.key === 'ArrowDown' ? 'first' : 'last';
      if (openGroup === group) {
        focusEdge(group, edge);
        return;
      }
      pendingEdge.current = edge;
      setOpenGroup(group);
      return;
    }
    if (event.key === 'Escape' && openGroup === group) {
      event.preventDefault();
      setOpenGroup(null);
    }
  };

  const onRowKeyDown = (
    event: React.KeyboardEvent<HTMLAnchorElement>,
    group: NavGroupKey,
    index: number,
  ) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusRow(group, index + 1);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusRow(group, index - 1);
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      focusEdge(group, 'first');
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      focusEdge(group, 'last');
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      closeAndReturnFocus(group);
    }
  };

  return (
    <div className="pa-shell">
      <header className="pa-header">
        <div className="pa-header-inner">
          <p className="pa-header-title">{title}</p>
          <div className="pa-header-identity">
            <span className="pa-header-name">{name}</span>
            <Button
              onClick={() => {
                setLeaving(true);
                void signOut().finally(() => setLeaving(false));
              }}
              loading={leaving}
            >
              {t('shell.logout')}
            </Button>
          </div>
        </div>
      </header>

      <nav className="pa-nav" aria-label={title}>
        <div className="pa-nav-inner">
          <ul className="pa-nav-bar">
            {NAV_BAR_ENTRIES.map((entry) => {
              // The nav key IS the route kind (nav.ts derives one from the other), so there is
              // no second table to keep in step — which is what a per-entry `if` chain was, and
              // it silently sent every unrecognised entry home.
              const target: Route = { kind: entry.key };
              return (
                <li key={entry.key}>
                  <a
                    className="pa-nav-top"
                    href={routePath(target)}
                    aria-current={active === entry.key ? 'page' : undefined}
                    onClick={(event) => go(event, target)}
                  >
                    {t(entry.labelKey)}
                  </a>
                </li>
              );
            })}
            {NAV_GROUPS.map((group) => (
              <li key={group.key}>
                <button
                  type="button"
                  id={buttonDomId(group.key)}
                  className="pa-nav-top pa-nav-group"
                  ref={(node) => {
                    groupButtons.current[group.key] = node;
                  }}
                  aria-expanded={openGroup === group.key}
                  aria-controls={panelDomId(group.key)}
                  /* ⚠ `aria-current` ON A BUTTON, and only when it is true. It is the ONLY
                     channel that survives the subscriber opening a DIFFERENT group: the panel
                     holding the marked link is closed then, and without this a screen-reader
                     user would have nothing in the tree saying which section he is on. React
                     omits the attribute for `undefined`, which matters — `aria-current="false"`
                     is a match for the `[aria-current]` selector that paints it. */
                  aria-current={activeGroup === group.key ? 'true' : undefined}
                  onClick={() => setOpenGroup(openGroup === group.key ? null : group.key)}
                  onKeyDown={(event) => onGroupKeyDown(event, group.key)}
                >
                  {t(group.labelKey)}
                </button>
              </li>
            ))}
          </ul>

          {/* The panels are SIBLINGS of the bar rather than children of their `<li>`, which is
              what lets one span the full width under a wrapped bar without any positioning
              arithmetic — at 360 px and at 1600 px, from the same markup. `aria-controls` and
              `aria-labelledby` carry the relationship the nesting no longer states. All five
              are always rendered so `aria-controls` always resolves; four of them are `hidden`,
              which is what keeps them out of the accessibility tree and out of the tests. */}
          {NAV_GROUPS.map((group) => (
            <ul
              key={group.key}
              id={panelDomId(group.key)}
              className="pa-nav-panel"
              aria-labelledby={buttonDomId(group.key)}
              hidden={openGroup !== group.key}
            >
              {group.entries.map((entry, index) => {
                const target: Route = { kind: entry.key };
                return (
                  <li key={entry.key}>
                    <a
                      href={routePath(target)}
                      aria-current={active === entry.key ? 'page' : undefined}
                      onClick={(event) => go(event, target)}
                      onKeyDown={(event) => onRowKeyDown(event, group.key, index)}
                    >
                      {t(entry.labelKey)}
                    </a>
                  </li>
                );
              })}
            </ul>
          ))}
        </div>
      </nav>

      <main className="pa-main">{children}</main>

      <footer className="pa-footer">
        <div className="pa-footer-inner">
          <span>{config.company_info || t('shell.footerTitle')}</span>
          {/* The support contacts, each only when the operator has written it. A subscriber
              reading this footer inside the walled garden has no other way to reach a
              human, which is why they are here and not on a «Контакты» page he cannot get
              to. */}
          {config.support_phone ? (
            <span>
              {t('shell.supportPhone')}: <a href={`tel:${config.support_phone}`}>{config.support_phone}</a>
            </span>
          ) : null}
          {config.support_email ? (
            <span>
              {t('shell.supportEmail')}:{' '}
              <a href={`mailto:${config.support_email}`}>{config.support_email}</a>
            </span>
          ) : null}
          {/* The fallback sentence, and ONLY when he has written neither contact —
              otherwise the footer would tell a subscriber to look in his contract while a
              telephone number sits beside it. */}
          {!config.support_phone && !config.support_email ? (
            <span>{t('shell.footerSupport')}</span>
          ) : null}
          {config.copyright_text ? <span>{config.copyright_text}</span> : null}
        </div>
      </footer>
    </div>
  );
}
